import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@tsuyosa/auth';

export function RegisterPage() {
  const { register, resendVerification, user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm]       = useState({ email: '', password: '', username: '', display_name: '' });
  const [error, setError]     = useState('');
  const [isLoading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMsg, setResendMsg] = useState('');

  if (user) { navigate('/'); return null; }

  function setField(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register({
        email: form.email,
        password: form.password,
        username: form.username,
        display_name: form.display_name || undefined,
      });
      setRegistered(true);
      startCooldown();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registrasi gagal');
    } finally { setLoading(false); }
  }

  function startCooldown() {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  async function handleResend() {
    setResendMsg('');
    try {
      await resendVerification(form.email);
      setResendMsg('Email verifikasi sudah dikirim ulang.');
      startCooldown();
    } catch (err) {
      setResendMsg(err instanceof Error ? err.message : 'Gagal mengirim ulang email');
    }
  }

  const inputClass =
    'w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-[#f472b6]/50 focus:ring-1 focus:ring-[#f472b6]/30';

  if (registered) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#0d0a12] px-4 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gradient-tsuyosa">TSUN</h1>
            <p className="mt-1 text-sm text-white/40">Tsuyosa Fan Portal</p>
          </div>

          <div className="rounded-xl border border-white/5 bg-[#130d1a] p-6 text-center">
            <div className="mb-4 flex justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#f472b6]/15 text-3xl">
                ✉
              </span>
            </div>
            <h2 className="mb-2 text-lg font-semibold text-white">Cek email kamu!</h2>
            <p className="mb-1 text-sm text-white/60">
              Kami sudah mengirim link verifikasi ke
            </p>
            <p className="mb-5 text-sm font-medium text-[#f472b6]">{form.email}</p>
            <p className="mb-5 text-xs text-white/40">
              Klik link di email untuk mengaktifkan akun kamu. Cek folder spam jika tidak muncul.
            </p>

            {resendMsg && (
              <p className="mb-3 rounded-lg bg-white/5 px-4 py-2 text-xs text-white/60">
                {resendMsg}
              </p>
            )}

            <button
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="w-full rounded-lg border border-white/10 bg-white/5 py-2.5 text-sm text-white/70 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {resendCooldown > 0 ? `Kirim ulang (${resendCooldown}s)` : 'Kirim ulang email verifikasi'}
            </button>
          </div>

          <p className="mt-4 text-center text-sm text-white/40">
            Sudah verifikasi?{' '}
            <Link to="/login" className="text-[#f472b6] hover:underline">
              Masuk di sini
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#0d0a12] px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gradient-tsuyosa">TSUN</h1>
          <p className="mt-1 text-sm text-white/40">Tsuyosa Fan Portal</p>
        </div>

        <div className="rounded-xl border border-white/5 bg-[#130d1a] p-6">
          <h2 className="mb-5 text-lg font-semibold text-white">Buat Akun</h2>

          {error && (
            <p className="mb-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </p>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="mb-1.5 block text-sm text-white/60">Email</label>
              <input type="email" required value={form.email} onChange={setField('email')} className={inputClass} placeholder="email@example.com" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-white/60">Username</label>
              <input type="text" required value={form.username} onChange={setField('username')} className={inputClass} placeholder="username" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-white/60">Nama Tampilan <span className="text-white/30">(opsional)</span></label>
              <input type="text" value={form.display_name} onChange={setField('display_name')} className={inputClass} placeholder="Nama yang ditampilkan" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm text-white/60">Password</label>
              <input type="password" required minLength={8} value={form.password} onChange={setField('password')} className={inputClass} placeholder="Min. 8 karakter" />
            </div>
            <button
              type="submit" disabled={isLoading}
              className="mt-1 flex w-full items-center justify-center rounded-lg bg-[#f472b6] py-2.5 text-sm font-semibold text-white shadow-[0_0_12px_rgba(244,114,182,0.35)] transition-all hover:bg-[#ec4899] hover:shadow-[0_0_20px_rgba(244,114,182,0.6)] disabled:opacity-50"
            >
              {isLoading
                ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                : 'Daftar'
              }
            </button>
          </form>
        </div>

        <p className="mt-4 text-center text-sm text-white/40">
          Sudah punya akun?{' '}
          <Link to="/login" className="text-[#f472b6] hover:underline">
            Masuk di sini
          </Link>
        </p>
      </div>
    </div>
  );
}
